import React, { useState, useEffect, useCallback, useMemo } from "react";
import Overridable from "react-overridable";
import { Modal, Message, Button, Header } from "semantic-ui-react";
import PropTypes from "prop-types";
import { sessionManager } from "@authentication/services/SessionManager";
import { loanApi } from "@api/loans";
import { isLibrarianOrAdmin } from "UserRoleUtils";
import SlipForm from "./SlipForm/SlipForm";
import LoanRequestSummary from "./SlipForm/LoanRequestSummary";
import { LoanInformationBullets } from "../DocumentCirculation/LoanInformationBullets";
import { AuditService } from "@api/audit/audit";
import { useHistory } from "react-router-dom";

const CONSTANTS = {
  FLOATING_ERROR_DURATION: 7000,
  DEFAULT_LOAN_DAYS: 3,
  DELIVERY_METHODS: {
    DELIVERY: "DELIVERY",
    PICKUP: "PICKUP",
  },
  ROUTES: {
    LOANS: "/loans",
    PATRON_PROFILE: "/profile",
  },
  COLORS: {
    PRIMARY_BUTTON_BG: "#3D7652",
    HEADER_COLOR: "#2C6E49",
    HEADER_BG: "#f8f9fa",
  },
  MESSAGES: {
    SUCCESS: {
      HEADER: "Request created",
      CONTENT: "Your request to loan this literature was created.",
    },
    ERROR: {
      GENERAL: "An error occurred while submitting the request",
      VALIDATION_HEADER: "Please correct the following errors:",
      DOCUMENT_ID_REQUIRED: "Document ID is required",
      PROFILE_ID_REQUIRED: "Patron ID is required. Please log in again.",
      VALIDATION_FAILED: "Validation failed",
      USAGE_SELECTION: "Please select either Home Use or Library Use",
    },
  },
  USAGE_TYPES: {
    HOME: "home",
    LIBRARY: "library",
  },
  MODAL_STATES: {
    FORM: "form",
    SUMMARY: "summary",
    SUCCESS: "success",
  },
};

// Function to get required fields based on user role
const getRequiredFields = (user) => {
  const baseRequiredFields = [
    { key: "firstName", label: "First Name" },
    { key: "lastName", label: "Last Name" },
    { key: "contactNo", label: "Contact Number" },
    { key: "college", label: "College" },
    { key: "program", label: "Program" },
  ];
  // Check if user is faculty, visitor, or researcher by looking at userType array
  const isNonStudent = user?.userType?.some((type) => {
    const userType = type.user_type?.toLowerCase();
    return (
      userType === "faculty" ||
      userType === "visitor" ||
      userType === "researcher"
    );
  });
  // Check if user is librarian or admin using existing function
  const isLibrarianAdmin = user?.roles && isLibrarianOrAdmin(user);
  // Only add year and semester as required for students (non-faculty/librarian/visitor/researcher users)
  if (!isNonStudent && !isLibrarianAdmin) {
    baseRequiredFields.push(
      { key: "year", label: "Year" },
      { key: "semester", label: "Semester" }
    );
  }
  return baseRequiredFields;
};

const utils = {
  date: {
    formatDate: (date) => date.toISOString().split("T")[0],
    // Add business days (excluding weekends)
    addBusinessDays: (date, days) => {
      const result = new Date(date);
      let addedDays = 0;
      while (addedDays < days) {
        result.setDate(result.getDate() + 1);
        // Skip weekends (Saturday = 6, Sunday = 0)
        if (result.getDay() !== 0 && result.getDay() !== 6) {
          addedDays++;
        }
      }
      return result;
    },
    // Calculate due date based on checkout date
    calculateDueDate: (
      checkoutDate,
      businessDays = CONSTANTS.DEFAULT_LOAN_DAYS
    ) => {
      const checkout = new Date(checkoutDate);
      return utils.date.addBusinessDays(checkout, businessDays);
    },
    // Legacy function for backward compatibility
    addDays: (date, days) => {
      return utils.date.addBusinessDays(date, days);
    },
  },
  user: {
    parseUserName: (fullName) => {
      if (!fullName?.trim()) {
        return { firstName: "", lastName: "" };
      }
      const nameParts = fullName.trim().split(/\s+/);
      if (nameParts.length === 1) {
        return { firstName: nameParts[0], lastName: "" };
      }
      return {
        firstName: nameParts.slice(0, -1).join(" "),
        lastName: nameParts[nameParts.length - 1],
      };
    },
  },
  validation: {
    // Updated validation function that accepts user parameter
    validateFormData: (formData, user) => {
      const requiredFields = getRequiredFields(user);
      const fieldErrors = requiredFields
        .filter(({ key }) => !formData[key]?.trim())
        .map(({ label }) => `${label} is required`);
      const usageError =
        !formData.homeUse && !formData.libraryUse
          ? [CONSTANTS.MESSAGES.ERROR.USAGE_SELECTION]
          : [];
      return [...fieldErrors, ...usageError];
    },
  },
  error: {
    getErrorMessage: (localError, apiError) => {
      return (
        localError ||
        apiError?.response?.data?.message ||
        CONSTANTS.MESSAGES.ERROR.GENERAL
      );
    },
    createEnrichedFormData: (baseData, document) => ({
      ...baseData,
      title: document?.metadata?.title || "Unknown Title",
    }),
  },
};

const useFormState = (initialFormData) => {
  const [state, setState] = useState({
    formData: initialFormData,
    modalOpen: false,
    modalState: CONSTANTS.MODAL_STATES.FORM,
    validationErrors: [],
    showFloatingError: false,
    isSubmitting: false,
    localError: null,
    success: false,
  });

  const actions = useMemo(
    () => ({
      // Form actions
      updateFormField: (name, value) => {
        setState((prev) => {
          const newFormData = { ...prev.formData, [name]: value };
          // Auto-calculate due date when checkout date changes
          if (name === "checkOutDate" && value) {
            if (prev.formData.libraryUse) {
              // For library use, due date is same as checkout date
              newFormData.dueDate = value;
            } else {
              // For home use, calculate due date with business days
              const dueDate = utils.date.calculateDueDate(value);
              newFormData.dueDate = utils.date.formatDate(dueDate);
            }
          }
          return {
            ...prev,
            formData: newFormData,
            validationErrors: [],
            showFloatingError: false,
            localError: null,
          };
        });
      },
      updateUsageType: (usageType) => {
        setState((prev) => {
          const newFormData = {
            ...prev.formData,
            homeUse: usageType === CONSTANTS.USAGE_TYPES.HOME,
            libraryUse: usageType === CONSTANTS.USAGE_TYPES.LIBRARY,
          };
          // If library use is selected, set due date same as checkout date
          if (
            usageType === CONSTANTS.USAGE_TYPES.LIBRARY &&
            prev.formData.checkOutDate
          ) {
            newFormData.dueDate = prev.formData.checkOutDate;
          }
          // If home use is selected, calculate due date with business days
          else if (
            usageType === CONSTANTS.USAGE_TYPES.HOME &&
            prev.formData.checkOutDate
          ) {
            const dueDate = utils.date.calculateDueDate(
              prev.formData.checkOutDate
            );
            newFormData.dueDate = utils.date.formatDate(dueDate);
          }
          return {
            ...prev,
            formData: newFormData,
            validationErrors: [],
            showFloatingError: false,
            localError: null,
          };
        });
      },
      enrichFormData: (document) => {
        setState((prev) => ({
          ...prev,
          formData: utils.error.createEnrichedFormData(prev.formData, document),
        }));
      },
      // Modal actions
      openModal: () => {
        setState((prev) => ({
          ...prev,
          modalOpen: true,
          modalState: CONSTANTS.MODAL_STATES.FORM,
          isSubmitting: false,
          localError: null,
          success: false,
          validationErrors: [],
          showFloatingError: false,
        }));
      },
      closeModal: () => {
        setState((prev) => ({
          ...prev,
          modalOpen: false,
          validationErrors: [],
          showFloatingError: false,
          localError: null,
        }));
      },
      showSummary: () => {
        setState((prev) => ({
          ...prev,
          modalState: CONSTANTS.MODAL_STATES.SUMMARY,
        }));
      },
      // Validation actions
      setValidationErrors: (errors) => {
        setState((prev) => ({
          ...prev,
          validationErrors: errors,
          showFloatingError: true,
        }));
      },
      hideFloatingError: () => {
        setState((prev) => ({
          ...prev,
          showFloatingError: false,
        }));
      },
      // Submission actions
      setSubmitting: (isSubmitting) => {
        setState((prev) => ({ ...prev, isSubmitting }));
      },
      setError: (error) => {
        setState((prev) => ({ ...prev, localError: error }));
      },
      setSuccess: () => {
        setState((prev) => ({
          ...prev,
          success: true,
          modalState: CONSTANTS.MODAL_STATES.SUCCESS,
        }));
      },
      clearError: () => {
        setState((prev) => ({ ...prev, localError: null }));
      },
    }),
    []
  );

  return [state, actions];
};

// Optimized FloatingErrorMessage component
const FloatingErrorMessage = React.memo(
  ({ errors, showFloatingError, onDismiss }) => {
    if (!showFloatingError || errors.length === 0) {
      return null;
    }
    return (
      <div
        style={{
          position: "fixed",
          top: "20px",
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 10000,
          maxWidth: "500px",
          width: "90%",
          animation: "fadeInDown 0.3s ease-out",
        }}
      >
        <Message
          negative
          onDismiss={onDismiss}
          style={{
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
            border: "1px solid #e0b4b4",
            borderRadius: "8px",
          }}
        >
          <Message.Header>
            {CONSTANTS.MESSAGES.ERROR.VALIDATION_HEADER}
          </Message.Header>
          <Message.List>
            {errors.map((error, index) => (
              <Message.Item key={index}>{error}</Message.Item>
            ))}
          </Message.List>
        </Message>
      </div>
    );
  }
);

FloatingErrorMessage.displayName = "FloatingErrorMessage";
FloatingErrorMessage.propTypes = {
  errors: PropTypes.arrayOf(PropTypes.string).isRequired,
  showFloatingError: PropTypes.bool.isRequired,
  onDismiss: PropTypes.func.isRequired,
};

const getInitialUserData = (user) => {
  if (!user) {
    return {
      firstName: "",
      lastName: "",
      contactNo: "",
      college: "",
      program: "",
      programCode: "",
      year: "",
      semester: "",
    };
  }
  const { firstName, lastName } = utils.user.parseUserName(user.fullName);
  return {
    firstName,
    lastName,
    contactNo: user.contactNumber || "",
    college: user.department || "",
    program: user.program || "",
    programCode: user.programCode || "",
    year: user.year || "",
    semester: user.semester || "",
  };
};

const LoanRequestForm = ({
  document,
  lastLoan,
  initializeState,
  history: historyProp,
  error,
  hasError,
  isSuccessful,
}) => {
  const history = useHistory();
  const [state, actions] = useFormState({
    ...getInitialUserData(sessionManager?.user || {}),
    checkOutDate: utils.date.formatDate(new Date()),
    dueDate: utils.date.formatDate(new Date()),
    homeUse: false,
    libraryUse: true,
  });

  const handleOpenModal = useCallback(() => {
    const user = sessionManager?.user || {};
    const userData = getInitialUserData(user);
    // Update form data with latest user info
    Object.entries(userData).forEach(([key, value]) => {
      actions.updateFormField(key, value);
    });
    actions.openModal();
  }, [actions.openModal, actions.updateFormField]);

  useEffect(() => {
    initializeState();
  }, [initializeState]);

  useEffect(() => {
    if (state.showFloatingError) {
      const timer = setTimeout(() => {
        actions.hideFloatingError();
      }, CONSTANTS.FLOATING_ERROR_DURATION);
      return () => clearTimeout(timer);
    }
  }, [state.showFloatingError, actions.hideFloatingError]);

  const navigateToRoute = useCallback(
    (route, state = null) => {
      history.push(route, state);
    },
    [history]
  );

  const handleInputChange = useCallback(
    (e, { name, value, checked, type, programCode }) => {
      const inputValue = type === "checkbox" ? checked : value;
      actions.updateFormField(name, inputValue);
      if (name === "program" && programCode !== undefined) {
        actions.updateFormField("programCode", programCode);
      }
    },
    [actions.updateFormField]
  );

  const handleLoanRequest = useCallback(
    async (formData) => {
      const user = sessionManager?.user || {};
      const errors = utils.validation.validateFormData(formData, user);
      if (errors.length > 0) {
        actions.setValidationErrors(errors);
        throw new Error(CONSTANTS.MESSAGES.ERROR.VALIDATION_FAILED);
      }
      const documentPid = document?.metadata?.pid;
      if (!documentPid) {
        throw new Error(CONSTANTS.MESSAGES.ERROR.DOCUMENT_ID_REQUIRED);
      }
      const patronPid = formData.patronPid || sessionManager?.user?.id;
      if (!patronPid) {
        throw new Error(CONSTANTS.MESSAGES.ERROR.PROFILE_ID_REQUIRED);
      }
      const requestParams = {
        requestStartDate: formData.checkOutDate,
        requestExpireDate: formData.dueDate,
        deliveryMethod: formData.homeUse
          ? CONSTANTS.DELIVERY_METHODS.DELIVERY
          : CONSTANTS.DELIVERY_METHODS.PICKUP,
        borrowerInfo: {
          firstName: formData.firstName,
          lastName: formData.lastName,
          contactNo: formData.contactNo,
          college: formData.college,
          program: formData.program,
          programCode: formData.programCode,
          year: formData.year,
          semester: formData.semester,
        },
      };
      return await loanApi.doRequest(documentPid, patronPid, requestParams);
    },
    [document, actions.setValidationErrors]
  );

  const handleSubmit = useCallback(async () => {
    if (state.isSubmitting) return;
    const user = sessionManager?.user || {};
    const errors = utils.validation.validateFormData(state.formData, user);
    if (errors.length > 0) {
      actions.setValidationErrors(errors);
      return;
    }
    actions.setSubmitting(true);
    actions.clearError();
    try {
      await handleLoanRequest(state.formData);
      actions.enrichFormData(document);
      actions.setSuccess();
      actions.showSummary();
      // --- AUDIT LOG INSERTION ---
      try {
        const userName =
          sessionManager?.user?.fullName ||
          sessionManager?.user?.username ||
          "Unknown User";
        const documentTitle = document?.metadata?.title || "Unknown Title";
        const auditData = {
          action: "Loan Request",
          details: `${userName} requested to loan '${documentTitle}'`,
          created_by: userName,
        };
        await AuditService.api.createLog(auditData);
      } catch (auditError) {
        // Audit log failed - could be handled silently or with a non-blocking notification
      }
    } catch (error) {
      const errorMessage = utils.error.getErrorMessage(null, error);
      actions.setError(errorMessage);
      actions.setValidationErrors([errorMessage]);
    } finally {
      actions.setSubmitting(false);
    }
  }, [
    state.formData,
    state.isSubmitting,
    handleLoanRequest,
    document,
    actions,
  ]);

  // Make the active tab stateful based on location state is activy tab just like this state: { focusTab: 'activity' }
  const handleViewLoans = useCallback(() => {
    actions.closeModal();
    navigateToRoute(CONSTANTS.ROUTES.PATRON_PROFILE, {
      focusTab: "activity",
    });
  }, [actions.closeModal, navigateToRoute]);

  const displayError = hasError || state.localError;
  const errorMessage = utils.error.getErrorMessage(state.localError, error);
  const showSuccess = isSuccessful || state.success;

  // Determine modal size based on state
  const modalSize =
    state.modalState === CONSTANTS.MODAL_STATES.SUMMARY ? "small" : "large";

  return (
    <>
      {showSuccess && state.modalState !== CONSTANTS.MODAL_STATES.SUMMARY ? (
        <Message
          positive
          icon="check"
          header={CONSTANTS.MESSAGES.SUCCESS.HEADER}
          content={CONSTANTS.MESSAGES.SUCCESS.CONTENT}
        />
      ) : !showSuccess ? (
        <>
          <LoanInformationBullets
            circulation={document.metadata.circulation}
            lastLoan={lastLoan}
          />
          {displayError && (
            <Message negative header="Error" content={errorMessage} />
          )}
          <Button
            data-tour="loan-request-button"
            primary
            fluid
            style={{
              backgroundColor: CONSTANTS.COLORS.PRIMARY_BUTTON_BG,
              color: "white",
              display: isLibrarianOrAdmin(sessionManager?.user)
                ? "none"
                : "block",
            }}
            onClick={handleOpenModal}
          >
            Fill Borrower's Slip Form
          </Button>
        </>
      ) : null}
      <FloatingErrorMessage
        errors={state.validationErrors}
        showFloatingError={state.showFloatingError}
        onDismiss={actions.hideFloatingError}
      />
      <Modal
        open={state.modalOpen}
        onClose={actions.closeModal}
        size={modalSize}
        closeIcon
      >
        <Modal.Header
          style={{
            textAlign: "center",
            backgroundColor: CONSTANTS.COLORS.HEADER_BG,
          }}
        >
          <Header
            as="h2"
            style={{
              color: CONSTANTS.COLORS.HEADER_COLOR,
              margin: 0,
            }}
          >
            {state.modalState === CONSTANTS.MODAL_STATES.SUMMARY
              ? "LOAN REQUEST SUMMARY"
              : "BORROWER'S SLIP FORM"}
          </Header>
        </Modal.Header>
        <Modal.Content>
          {state.modalState === CONSTANTS.MODAL_STATES.SUMMARY ? (
            <LoanRequestSummary
              formData={state.formData}
              document={document}
              onViewLoans={handleViewLoans}
              onGoBack={actions.closeModal}
            />
          ) : state.success ? (
            <Message
              positive
              icon="check"
              header={CONSTANTS.MESSAGES.SUCCESS.HEADER}
              content={CONSTANTS.MESSAGES.SUCCESS.CONTENT}
            />
          ) : (
            <SlipForm
              formData={state.formData}
              document={document}
              hasError={false}
              error={{}}
              isSuccessful={false}
              user={sessionManager?.user}
              onInputChange={handleInputChange}
              onDropdownChange={handleInputChange}
              onUsageChange={actions.updateUsageType}
            />
          )}
        </Modal.Content>
        {state.modalState === CONSTANTS.MODAL_STATES.FORM && !state.success && (
          <Modal.Actions>
            <Button
              color="red"
              onClick={actions.closeModal}
              disabled={state.isSubmitting}
            >
              Cancel
            </Button>
            <Button
              color="green"
              onClick={handleSubmit}
              loading={state.isSubmitting}
              disabled={state.isSubmitting}
            >
              Submit Request
            </Button>
          </Modal.Actions>
        )}
      </Modal>
      <style>
        {`
          @keyframes fadeInDown {
            from {
              opacity: 0;
              transform: translateX(-50%) translateY(-20px);
            }
            to {
              opacity: 1;
              transform: translateX(-50%) translateY(0);
            }
          }
        `}
      </style>
    </>
  );
};

LoanRequestForm.propTypes = {
  document: PropTypes.object.isRequired,
  lastLoan: PropTypes.string,
  initializeState: PropTypes.func.isRequired,
  history: PropTypes.object,
  error: PropTypes.object.isRequired,
  hasError: PropTypes.bool.isRequired,
  isSuccessful: PropTypes.bool.isRequired,
};

LoanRequestForm.defaultProps = {
  lastLoan: null,
  history: null,
};

export default Overridable.component("LoanRequestForm", LoanRequestForm);
